import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider } from "@/context/auth-context";
import { appRoutes } from "./App";
import "./styles/globals.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Treat data as fresh for 5 min — most of what we fetch (agents,
      // companies, catalogues) rarely changes mid-session. Cuts the
      // refetch-on-every-nav churn that made /dashboard and tenant
      // pages feel slow.
      staleTime: 5 * 60_000,
      // Keep cached data around for 30 min after the last subscriber
      // unmounts so back/forward navigation hits cache instead of network.
      gcTime: 30 * 60_000,
      // Don't refire every query when the user tabs back to the window.
      // Mutations already invalidate the specific keys that matter.
      refetchOnWindowFocus: false,
    },
  },
});

// A data router, not <BrowserRouter>. Only this flavour supports useBlocker,
// which the workflow canvas needs to stop a navigation while there are unsaved
// changes. Built once at module scope: rebuilding it on a render would reset
// history and remount every page.
const router = createBrowserRouter(appRoutes);

// Theme and auth moved OUTSIDE the router, which is the reverse of the old
// nesting. Neither reads router context (no useNavigate, useLocation or
// useParams in either), and a data router is created before React renders, so
// its providers cannot wrap it from the inside. Pages still sit inside both.
ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
